import { confirm, alert } from 'devextreme/ui/dialog';
import { MESSAGES } from '../../../../../common/Messages';
import { LocationByClass, TransactionTypes } from '../../../../../enum/Enum';

// Reserve Location class check.
//
// The item's Reserve Location class and sub class codes must match the location's. The transaction data
// profile's LOCATIONBYCLASS decides how hard the rule bites — 'Y' refuses the location, 'W' asks first,
// anything else switches the check off.
//
// Delphi gates the whole rule on RECEIPTS: edtP_LocationAfterDataValidation opens with
// `if (DM.FTransactionType = 'R') and (LOCATIONBYCLASS in ('W','Y'))` (uWMD0002.pas:12896), and on a
// mismatch calls Abort — so 'Y' always rejects and 'W' rejects only when the user cancels. It therefore
// never fires on a shipment, which is why the manual selection screens — shipment-only by their
// pre-conditions — do not call this at all.
//
// Used by the Location column editors in SubLotDetailTabs and PrimaryTab, so the rule has one home and
// cannot drift between them.

const norm = (value) => (value ?? '').trim();

/** True when the rule does not apply: receipts only, and only for the two enforcing settings. */
export function isLocationClassCheckDisabled(locationByClass, transactionType) {
    if (transactionType !== TransactionTypes.Receipt) return true;
    return locationByClass !== LocationByClass.Error && locationByClass !== LocationByClass.Warn;
}

/**
 * Builds the mismatch message for one location, or null when it is acceptable.
 * A location carrying neither a class nor a sub class code is acceptable, matching the grid editors,
 * which only report a mismatch when the location actually has one of them.
 */
export function getLocationClassViolation(itemClassCode, itemSubClassCode, locationClassCode, locationSubClassCode) {
    const locClass = norm(locationClassCode);
    const locSubClass = norm(locationSubClassCode);

    if (!locClass && !locSubClass) return null;

    const matches = norm(itemClassCode) === locClass && norm(itemSubClassCode) === locSubClass;
    if (matches) return null;

    return MESSAGES.LOCATION_CLASS_MISMATCH
        .replace('{0}', norm(itemClassCode))
        .replace('{1}', norm(itemSubClassCode))
        .replace('{2}', locClass)
        .replace('{3}', locSubClass);
}

/**
 * Applies the rule to a location the user has just picked. Returns false when the location must be
 * refused, so the caller can skip applying it and leave the cell on its previous value.
 */
export async function checkLocationClassOnPick({
    itemClassCode, itemSubClassCode, locationClassCode, locationSubClassCode, locationByClass, transactionType,
}) {
    if (isLocationClassCheckDisabled(locationByClass, transactionType)) return true;

    const violation = getLocationClassViolation(
        itemClassCode, itemSubClassCode, locationClassCode, locationSubClassCode);

    if (!violation) return true;

    if (locationByClass === LocationByClass.Warn)
        return await confirm(`<i>${violation}</i>`, MESSAGES.CONFIRM);

    await alert(violation, MESSAGES.ERROR);
    return false;
}
